import * as _ from "lodash-es";

export const SOCIAL_AUTH_TYPES = ["google", "linkedin", "facebook"];
export const STORAGE_KEY = "admissionalOnboardingContext";

export function isSocialMediaAccount(user) {
    return _.indexOf(SOCIAL_AUTH_TYPES, user?.auth_type) >= 0;
}

export function isMissingCpf(user) {
    return _.isEmpty(user?.cpf);
}

export function needsPasswordSetup(user) {
    return user?.need_to_change_password && !isSocialMediaAccount(user);
}

/**
 * First pending credential step for password-based accounts (pwd/email).
 * Social logins skip password setup.
 *
 * @returns {"cpf"|"password"|null}
 */
export function getFirstCredentialSetupStep(user) {
    if (isMissingCpf(user)) {
        return "cpf";
    }

    if (needsPasswordSetup(user)) {
        return "password";
    }

    return null;
}

/**
 * Account setup used by welcome / profile-edit:
 * CPF → termos → senha (pwd). Social skips password.
 *
 * @returns {Promise<"cpf"|"terms"|"password"|null>}
 */
export async function getFirstAccountSetupStep(user, dashboard) {
    if (isMissingCpf(user)) {
        return "cpf";
    }

    if (await isMissingTerms(dashboard)) {
        return "terms";
    }

    if (needsPasswordSetup(user)) {
        return "password";
    }

    return null;
}

export function buildCredentialSetupRoute(step, query = {}) {
    switch (step) {
        case "cpf":
            return { name: "complete-registration", query };
        case "password":
            return {
                name: "register4",
                params: { change_password: true },
                query,
            };
        default:
            return null;
    }
}

export function isMissingEmail(user) {
    return _.isEmpty(user?.email);
}

export async function isMissingTerms(dashboard) {
    const response = await dashboard.termsAndConditionsByUser();
    return !response.data.success;
}

export async function getFirstOnboardingStep(user, dashboard) {
    const credentialStep = getFirstCredentialSetupStep(user);

    if (credentialStep) {
        return credentialStep;
    }

    if (isMissingEmail(user)) {
        return "email";
    }

    if (await isMissingTerms(dashboard)) {
        return "terms";
    }

    return null;
}

export function saveAdmissionalOnboardingContext(context) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
}

export function getAdmissionalOnboardingContext() {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    return raw ? JSON.parse(raw) : null;
}

export function clearAdmissionalOnboardingContext() {
    sessionStorage.removeItem(STORAGE_KEY);
}

export function isAdmissionalOnboardingActive() {
    return !!getAdmissionalOnboardingContext();
}

export function buildReturnRoute(context) {
    if (!context) {
        return { name: "profile-admissional" };
    }

    return {
        name: context.returnRoute || "profile-admissional",
        params: context.params || {},
        query: context.query || {},
    };
}

export function redirectToOnboardingStep(router, step, context) {
    const baseQuery = {
        ...(context?.query || {}),
        admissional_onboarding: "1",
    };

    const credentialRoute = buildCredentialSetupRoute(step, baseQuery);

    if (credentialRoute) {
        router.replace(credentialRoute);
        return;
    }

    switch (step) {
        case "email":
            router.replace({
                name: "register2",
                query: { ...baseQuery, admissional_flow: "email" },
            });
            break;
        case "terms":
            router.replace(buildReturnRoute(context));
            break;
        default:
            router.replace(buildReturnRoute(context));
    }
}

export async function continueAdmissionalOnboarding(router, auth, dashboard) {
    await auth.fetch();

    const context = getAdmissionalOnboardingContext();
    const step = await getFirstOnboardingStep(auth.user(), dashboard);

    if (!step) {
        clearAdmissionalOnboardingContext();
        router.replace(buildReturnRoute(context));
        return;
    }

    redirectToOnboardingStep(router, step, context);
}
